// CopyCat (Viral To Me) — viral-script analysis + AI adaptation panel.
// Replaces the legacy UGC Avatar / Voiceover panel. Premium creator-economy
// aesthetic: deep gradient surface, subtle glow, modern type, animated hover.
import React, { useMemo, useState } from "react";
import {
  Wand2, Loader2, Sparkles, Copy as CopyIcon, ExternalLink,
  Flame, Microscope, Zap, Megaphone, ClipboardPaste,
} from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { analyzeAndAdaptScript } from "../lib/copycat";

const TRANSCRIPT_URL = "https://litetranscript.com/";

const STRENGTH_OPTIONS = [
  { key: "minimal",    label: "Minimal",    note: "closest to original", accent: "from-emerald-400 to-teal-400" },
  { key: "balanced",   label: "Balanced",   note: "viral preserved",     accent: "from-cyan-400 to-violet-400" },
  { key: "aggressive", label: "Aggressive", note: "heavier rewrite",     accent: "from-fuchsia-500 to-amber-400" },
];

function copyText(text, label = "Copied") {
  if (!text) return;
  try {
    navigator.clipboard.writeText(text);
    toast.success(label);
  } catch (_) { /* noop */ }
}

function tryPasteFromClipboard(setValue) {
  if (!navigator?.clipboard?.readText) {
    toast.info("Clipboard not available — paste manually with Ctrl/⌘+V");
    return;
  }
  navigator.clipboard.readText()
    .then((t) => {
      if (!t || !t.trim()) {
        toast.info("Clipboard is empty");
        return;
      }
      setValue(t);
      toast.success("Pasted from clipboard");
    })
    .catch(() => toast.error("Clipboard blocked — paste manually"));
}

export const CopyCatPanel = ({ apiKey, ctaKeyword = "GO", ctaSentence = "", niche = "", audience = "", offer = "" }) => {
  const [pasted, setPasted] = useState("");
  const [strength, setStrength] = useState("balanced");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const charCount = pasted.length;
  const canGenerate = useMemo(
    () => !!apiKey && pasted.trim().length >= 30 && !loading,
    [apiKey, pasted, loading]
  );

  const generate = async () => {
    if (!apiKey) { toast.error("Connect your OpenAI key first"); return; }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await analyzeAndAdaptScript({
        apiKey,
        pastedScript: pasted,
        niche,
        audience,
        offer,
        ctaSentence,
        ctaKeyword: (ctaKeyword || "GO").trim().split(/\s+/)[0].toUpperCase(),
        strength,
      });
      setResult(r);
      toast.success("Viral adaptation ready");
    } catch (e) {
      setError(e.message || "Adaptation failed");
      toast.error(e.message || "Adaptation failed");
    } finally {
      setLoading(false);
    }
  };

  const energyColor = result?.viral_analysis?.energy_level === "hard"
    ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
    : result?.viral_analysis?.energy_level === "soft"
      ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
      : "text-amber-300 border-amber-500/40 bg-amber-500/10";

  return (
    <section
      data-testid="copycat-panel"
      className="mt-8 relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-950 via-[#0c0a16] to-zinc-950 p-6 sm:p-9"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full blur-3xl bg-gradient-to-br from-violet-500/20 via-fuchsia-500/15 to-amber-500/15" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-[360px] w-[360px] rounded-full blur-3xl bg-gradient-to-tr from-cyan-500/15 to-emerald-500/10" />

      {/* Header */}
      <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
        <div className="space-y-3 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.28em] text-violet-200 shadow-[0_0_28px_-6px_rgba(167,139,250,0.6)]">
            <Flame className="h-3 w-3" strokeWidth={2.5} />
            Viral analysis · AI adaptation
          </div>
          <h3 className="font-display text-3xl sm:text-[34px] font-black text-white leading-[1.05] tracking-tight">
            CopyCat{" "}
            <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-amber-300 bg-clip-text text-transparent">
              (Viral To Me)
            </span>
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-xl">
            Don't reinvent the wheel. Copy a winning script. AI identifies the
            viral components, preserves what makes it work, optimizes it for
            virality, and makes it your own.
          </p>
        </div>

        {/* Quick stats / pillars */}
        <div className="hidden lg:flex flex-col gap-1.5 text-right">
          <PillarTag icon={Microscope} text="Hook archetype detected" />
          <PillarTag icon={Zap}        text="Retention mechanic mapped" />
          <PillarTag icon={Megaphone}  text="Your CTA, auto-injected" />
        </div>
      </div>

      {/* Input card */}
      <div className="relative rounded-2xl border border-white/10 bg-zinc-950/70 backdrop-blur-sm p-5 sm:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-400">
              Paste viral script
            </label>
            <p className="text-[12px] text-zinc-500 mt-1 max-w-md">
              Paste any viral TikTok, Reel, or Shorts script and AI will adapt
              it for your offer while preserving the viral psychology.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => tryPasteFromClipboard(setPasted)}
              data-testid="copycat-paste-clipboard"
              className="group inline-flex items-center gap-1.5 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-zinc-300 hover:text-white text-[11px] font-semibold tracking-wide px-3 transition-all"
            >
              <ClipboardPaste className="h-3.5 w-3.5" strokeWidth={2} />
              Paste
            </button>
            <a
              href={TRANSCRIPT_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="copycat-get-script-link"
              className="group inline-flex items-center gap-1.5 h-8 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 hover:from-violet-500/30 hover:to-fuchsia-500/30 border border-violet-500/40 text-violet-100 hover:text-white text-[11px] font-semibold tracking-wide px-3 transition-all shadow-[0_0_20px_-6px_rgba(167,139,250,0.5)] hover:shadow-[0_0_28px_-4px_rgba(167,139,250,0.7)]"
            >
              <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={2} />
              Get Script
            </a>
          </div>
        </div>

        <div className="relative group">
          <Textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste the original spoken script here. Even rough transcripts work — the AI infers structure."
            data-testid="copycat-paste-input"
            className="min-h-[180px] bg-zinc-900/80 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-violet-500 focus:ring-violet-500/20 resize-y text-sm leading-relaxed transition-all"
          />
          <div className="pointer-events-none absolute right-3 bottom-3 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-600">
            {charCount}/6000
          </div>
        </div>

        {/* Transformation Strength */}
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-400 mb-2.5">
            Transformation strength
          </label>
          <div className="grid grid-cols-3 gap-2">
            {STRENGTH_OPTIONS.map((opt) => {
              const active = strength === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setStrength(opt.key)}
                  data-testid={`copycat-strength-${opt.key}`}
                  className={[
                    "group relative overflow-hidden rounded-xl border px-3 py-2.5 text-left transition-all",
                    active
                      ? "border-white/20 bg-white/[0.06] shadow-[0_0_24px_-6px_rgba(255,255,255,0.18)]"
                      : "border-white/10 bg-zinc-900/40 hover:border-white/20 hover:bg-zinc-900/70",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "absolute inset-x-0 -bottom-px h-[2px] bg-gradient-to-r transition-opacity",
                      opt.accent,
                      active ? "opacity-100" : "opacity-0 group-hover:opacity-60",
                    ].join(" ")}
                  />
                  <div className="text-[12.5px] font-bold text-white">
                    {opt.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-0.5">
                    {opt.note}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Generate button */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1">
          <p className="text-[11px] text-zinc-500 leading-relaxed max-w-md">
            Output preserves the original hook structure, pacing, and emotional
            curve — your locked CTA replaces the original.
          </p>
          <Button
            type="button"
            onClick={generate}
            disabled={!canGenerate}
            data-testid="copycat-generate-button"
            className="group relative h-12 px-6 rounded-full font-semibold text-sm text-white
                       bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-500
                       hover:from-violet-400 hover:via-fuchsia-400 hover:to-amber-400
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-300
                       shadow-[0_12px_36px_-12px_rgba(217,70,239,0.55)]
                       hover:shadow-[0_18px_48px_-10px_rgba(217,70,239,0.75)]
                       hover:-translate-y-[1px]
                       inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} /> Analysing & adapting…</>
            ) : (
              <><Wand2 className="h-4 w-4 transition-transform group-hover:rotate-12" strokeWidth={2.5} /> Generate Viral Adaptation</>
            )}
          </Button>
        </div>

        {error && (
          <div
            data-testid="copycat-error"
            className="rounded-xl border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[12px] text-rose-200"
          >
            {error}
          </div>
        )}
      </div>

      {/* Output */}
      {result && (
        <div className="relative mt-7 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Original hook */}
          <OutputCard
            label="Original Hook"
            accent="from-cyan-400 to-blue-400"
            testid="copycat-output-original-hook"
            value={result.original_hook}
            copyable
          />

          {/* Viral analysis */}
          <div
            data-testid="copycat-output-analysis"
            className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-zinc-950/70 to-zinc-950/70 p-5 space-y-3"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.28em] text-violet-300">
                <Microscope className="h-3 w-3" strokeWidth={2.5} />
                Viral Analysis
              </span>
              {result.viral_analysis?.energy_level && (
                <span className={`text-[10px] font-bold uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 ${energyColor}`}>
                  {result.viral_analysis.energy_level} energy
                </span>
              )}
            </div>
            <AnalysisRow label="Hook archetype"   value={result.viral_analysis?.hook_archetype} />
            <AnalysisRow label="Retention mechanic" value={result.viral_analysis?.retention_mechanic} />
            <AnalysisRow label="Emotional arc"     value={result.viral_analysis?.emotional_arc} />
            <AnalysisRow label="Why it worked"     value={result.viral_analysis?.why_it_worked} />
          </div>

          {/* Adapted script */}
          <OutputCard
            label="Adapted Script"
            accent="from-emerald-400 to-teal-400"
            testid="copycat-output-adapted"
            value={result.adapted_script}
            multiline
            copyable
            highlight
          />

          {/* CTA-injected version */}
          <CtaInjectedCard
            value={result.cta_injected_version}
            cta={result.cta_used}
          />
        </div>
      )}
    </section>
  );
};

// ─────────── small presentational helpers ───────────
const PillarTag = ({ icon: Icon, text }) => (
  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400">
    <Icon className="h-3 w-3 text-violet-300" strokeWidth={2.5} />
    {text}
  </span>
);

const AnalysisRow = ({ label, value }) => (
  <div className="space-y-1">
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-200/70">
      {label}
    </div>
    <div className="text-[13px] text-zinc-100 leading-snug">
      {value || "—"}
    </div>
  </div>
);

const OutputCard = ({ label, accent, testid, value, multiline = false, copyable = false, highlight = false }) => (
  <div
    data-testid={testid}
    className={[
      "relative overflow-hidden rounded-2xl border p-5 space-y-3 transition-all",
      highlight
        ? "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-zinc-950/70 to-zinc-950/70 shadow-[0_0_28px_-12px_rgba(16,185,129,0.45)]"
        : "border-white/10 bg-zinc-950/70",
    ].join(" ")}
  >
    <div className={`absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r ${accent}`} />
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-400">
        {label}
      </span>
      {copyable && value && (
        <button
          type="button"
          onClick={() => copyText(value, `${label} copied`)}
          className="inline-flex items-center gap-1 h-6 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white text-[10px] font-semibold tracking-wide px-2 transition-all"
        >
          <CopyIcon className="h-3 w-3" strokeWidth={2} />
          Copy
        </button>
      )}
    </div>
    <div className={[
      "text-[14px] text-zinc-100 leading-relaxed",
      multiline ? "whitespace-pre-wrap" : "",
    ].join(" ")}>
      {value || "—"}
    </div>
  </div>
);

const CtaInjectedCard = ({ value, cta }) => {
  // Render the script with the CTA portion highlighted.
  let nodes = null;
  if (value && value.includes("<<<") && value.includes(">>>")) {
    const [before, rest] = value.split("<<<");
    const [middle, after] = rest.split(">>>");
    nodes = (
      <>
        <span className="text-zinc-300">{before}</span>
        <span
          data-testid="copycat-output-cta-highlight"
          className="bg-amber-500/15 border border-amber-500/40 rounded-md px-1.5 py-0.5 text-amber-100 font-semibold"
        >
          {middle}
        </span>
        <span className="text-zinc-300">{after}</span>
      </>
    );
  } else {
    nodes = <span className="text-zinc-300">{value || "—"}</span>;
  }
  return (
    <div
      data-testid="copycat-output-cta-injected"
      className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-950/70 to-zinc-950/70 p-5 space-y-3 shadow-[0_0_28px_-12px_rgba(245,158,11,0.4)]"
    >
      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-amber-400 to-rose-400" />
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.28em] text-amber-300">
          <Megaphone className="h-3 w-3" strokeWidth={2.5} />
          CTA Injected Version
        </span>
        {value && (
          <button
            type="button"
            onClick={() => copyText(value.replace(/<<<|>>>/g, ""), "CTA-injected script copied")}
            className="inline-flex items-center gap-1 h-6 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white text-[10px] font-semibold tracking-wide px-2 transition-all"
          >
            <CopyIcon className="h-3 w-3" strokeWidth={2} />
            Copy
          </button>
        )}
      </div>
      <div className="text-[14px] leading-relaxed whitespace-pre-wrap">
        {nodes}
      </div>
      {cta && (
        <div className="text-[11px] text-amber-200/70 italic leading-snug border-t border-amber-500/20 pt-2.5 mt-2">
          <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" strokeWidth={2} />
          Locked CTA from your CTA Control Panel: <span className="text-amber-100 not-italic font-medium">"{cta}"</span>
        </div>
      )}
    </div>
  );
};

export default CopyCatPanel;
